import { t } from 'try';
import RpcClient from './RpcClient';

type SyncTx = {
	t0?: Date;
	t1: Date;
	t2: Date;
	t3?: Date;
}

export type Update = {
	clock_offset: number;
	net_delay: number;
	error: number;
	interval: number;
}

export let interval_min = 250;
export let interval_max = 4_000;
export let error_target = 0.01;

export class TimesyncClient {
	rpc: RpcClient;
	period: number;
	last_sync?: Date;
	sync_interval = 0;
	disposed = false;
	rtt = 0;
	clock_offset = 0;
	net_delay = 0;
	error = 0;
	subs = new Set<(update: Update) => void>();

	constructor(rpc: RpcClient, period = 10_000) {
		this.rpc = rpc;
		this.period = period;

		this.syncLoop();
	}

	dispose () {
		this.disposed = true;
		this.subs.clear();
	}

	subscribe (listenFn: (update: Update) => void) {
		this.subs.add(listenFn);
		return () => this.subs.delete(listenFn);
	}

	async syncLoop () {
		while (!this.disposed) {
			await this.sync();
			await sleep(this.sync_interval);
		}
	}

	async sync () {
		const t0 = new Date();
		const tx = performance.now(); // Better precision
		const [ok, err, rsp] = await t(this.rpc.request<SyncTx>('timesync.sync', { t0 }, { timeout: interval_max }));
		if (!ok) {
			console.error('error syncing:', err);
			return;
		}
		// console.log(rsp);
		const rx = performance.now();
		const t3 = new Date();
		const t1 = new Date(rsp.t1);
		const t2 = new Date(rsp.t2);

		const rtt = rx - tx;
		const clock_offset = ((+t1 - +t0) + (+t2 - +t3)) / 2;
		const net_delay = ((+t3 - +t0) - (+t2 - +t1)) / 2;

		// console.log({ rtt, clock_offset, net_delay });

		if (!this.last_sync) {
			this.rtt = rtt;
			this.clock_offset = clock_offset;
			this.net_delay = net_delay;
		} else {
			// Compare this sample's error to the average and adjust the sync rate accordingly
			// Use the error of the sum rather than the average error, since the HLC relies on the sum being accurate
			const error = hyb(Math.abs(this.clock_offset) + Math.abs(this.net_delay), Math.abs(clock_offset) + Math.abs(net_delay));

			// Take the max here because it may be impossible to reach the target based on the device and/or network conditions
			const target = Math.max(error_target, this.error);
			let sync_interval;
			if (error <= target) {
				// Increase the interval additively
				sync_interval = Math.min(this.sync_interval + interval_min, interval_max);
			} else {
				// Decrease the interval multiplicatively
				sync_interval = Math.max(interval_min, this.sync_interval / 1.25); // 2 is too dramatic; 1.25 (5/4) is based around our mins/maxes being powers of 4
			}
			// console.log('error:', error, 'target:', target, 'ratio:', target / error, 'sync interval:', this.sync_interval, '->', sync_interval);
			this.sync_interval = sync_interval;

			const dt = +t3 - +this.last_sync;
			const inertia = Math.exp(-dt / this.period);
			const weight = 1 - inertia;
			// console.log({ dt, inertia, weight });

			// Determine the sample quality by comparing this RTT to the average RTT; the lower this RTT is than average, the higher the quality
			let quality = this.rtt / rtt;
			// Bound quality asymptotically between (0, 2)
			quality = 1 + Math.tanh(quality - 1);
			// console.log({ rtt, this_rtt: this.rtt, quality });

			const quality_weight = quality * weight;
			const quality_inertia = 1 - quality_weight;
			// console.log({ quality_inertia, quality_weight });

			// Update averages
			// Don't apply quality to RTT update since we are using RTT to calculate quality
			this.rtt = this.rtt * inertia + rtt * weight;
			// But apply quality to other averages
			this.clock_offset = this.clock_offset * quality_inertia + clock_offset * quality_weight;
			this.net_delay = this.net_delay * quality_inertia + net_delay * quality_weight;
			this.error = this.error * quality_inertia + error * quality_weight;
		}

		// console.log({ clockOffset: this.clockOffset, netDelay: this.netDelay });
		this.last_sync = t3;

		for (const sub of this.subs) {
			sub({
				clock_offset: this.clock_offset,
				net_delay: this.net_delay,
				error: this.error,
				interval: this.sync_interval,
			});
		}
	}
}

// Hyb error: https://arxiv.org/abs/2403.07492
function hyb (expected: number, actual: number) {
	return Math.abs(actual - expected) / (1 + Math.abs(expected));
}

function sleep (ms: number) {
	return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

export { TimesyncClient as default };
